﻿import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Search, X, User, Car, ChevronRight, Phone, MapPin, Calendar,
  AlertTriangle, Eye, Droplets, BookOpen, Heart, Globe, CreditCard,
  Briefcase, Save, Plus, Edit2, CheckCircle, Upload, Download, FileText,
  AlertCircle, Trash2, ScanFace,
} from 'lucide-react';
import { deleteFaceFromHF } from '../lib/storage';
import { supabase } from '../lib/supabase';
import PhotoGallery from '../components/PhotoGallery';
import OsintTab from '../components/OsintTab';
import { useApp } from '../contexts/AppContext';
import { getIntelLabel } from '../lib/statusLabel';

// ── CSV helpers ───────────────────────────────────────────────────────────────

const CSV_HEADERS = [
  'nik','nama_lengkap','tempat_lahir','tanggal_lahir','jenis_kelamin',
  'gol_darah','agama','status_perkawinan','pekerjaan','kewarganegaraan',
  'alamat_lengkap','rt_rw','kel_desa','kecamatan','kota_kabupaten',
  'status_intelijen','catatan_fisik',
];

function parseCSVLine(line) {
  const result = [];
  let cur = '', inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') { inQ = !inQ; continue; }
    if (c === ',' && !inQ) { result.push(cur); cur = ''; continue; }
    cur += c;
  }
  result.push(cur);
  return result;
}

function parseCSV(text) {
  // Strip BOM Excel + abaikan kolom di luar CSV_HEADERS → cegah kolom tak
  // dikenal membuat upsert data:null (silent data-loss).
  const lines = text.replace(/^﻿/, '').replace(/\r/g, '').split('\n').filter(l => l.trim());
  if (lines.length < 2) return [];
  const allow = new Set(CSV_HEADERS);
  const headers = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());
  return lines.slice(1).map(line => {
    const vals = parseCSVLine(line);
    const obj = { status_intelijen: 'CLEAR' };
    headers.forEach((h, i) => {
      if (!allow.has(h)) return;
      obj[h] = vals[i]?.trim() || null;
    });
    return obj;
  }).filter(r => r.nik);
}

function downloadTemplate() {
  // CSV template — HEADER ONLY. JANGAN embed PII contoh (NIK/nama/alamat)
  // walaupun fictional, supaya operator gak salah submit data placeholder
  // ke production DB. Operator isi sendiri dari KTP scan / data resmi.
  const csv = CSV_HEADERS.join(',') + '\n';
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = 'template_penduduk.csv';
  a.click();
}

// ── Batch Upload Modal ────────────────────────────────────────────────────────

const BatchUploadModal = ({ onClose, onImported }) => {
  const { lang } = useApp();
  const [step, setStep]       = useState('upload'); // upload | preview | done
  const [rows, setRows]       = useState([]);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef();

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      const parsed = parseCSV(e.target.result);
      if (parsed.length === 0) return alert('File kosong atau format tidak sesuai. Download template terlebih dahulu.');
      setRows(parsed);
      setStep('preview');
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    setImporting(true);
    const CHUNK = 50;
    let success = 0, failed = 0, failedRows = [];
    for (let i = 0; i < rows.length; i += CHUNK) {
      const chunk = rows.slice(i, i + CHUNK);
      // .select() → hitung baris yang benar-benar ditulis (data:null = 0 baris).
      const { data, error } = await supabase.from('penduduk').upsert(chunk, { onConflict: 'nik' }).select('nik');
      const wrote = data?.length || 0;
      if (error || wrote === 0) { failed += chunk.length; failedRows.push(...chunk); }
      else { success += wrote; if (wrote < chunk.length) failed += chunk.length - wrote; }
    }
    setImporting(false);
    setResults({ success, failed, failedRows });
    setStep('done');
    if (success > 0) onImported(rows.filter(r => !failedRows.includes(r)));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: 'var(--overlay)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-2xl rounded-3xl overflow-hidden"
        style={{ background: 'var(--color-card)', border: '1px solid var(--border-mid)', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 shrink-0" style={{ borderBottom: '1px solid var(--border-dim)' }}>
          <div className="flex items-center gap-3">
            <Download size={16} color="var(--color-cyan)" />
            <p className="text-xs font-black uppercase tracking-widest font-mono" style={{ color: 'var(--text-dim)' }}>
              {step === 'upload' ? 'Import Data Penduduk' : step === 'preview' ? `Preview — ${rows.length} data` : 'Hasil Import'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadTemplate}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black"
              style={{ background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.2)', color: 'var(--color-green)' }}>
              <Download size={10} /> Template CSV
            </button>
            <button onClick={onClose} className="hover:opacity-70"><X size={14} color="var(--text-dim)" /></button>
          </div>
        </div>

        {/* Step: Upload */}
        {step === 'upload' && (
          <div className="flex-1 flex flex-col items-center justify-center p-8 gap-6">
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
              className="w-full flex flex-col items-center justify-center gap-4 rounded-2xl cursor-pointer transition-all"
              style={{ height: 220, border: `2px dashed ${dragging ? 'var(--color-cyan)' : 'var(--border-mid)'}`, background: dragging ? 'rgba(0,212,255,0.05)' : 'var(--color-surface)' }}>
              <FileText size={48} color="rgba(0,212,255,0.3)" strokeWidth={1} />
              <div className="text-center">
                <p className="text-sm font-bold" style={{ color: 'var(--text-dim)' }}>Seret file CSV ke sini atau klik untuk pilih</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-mute)' }}>Format: CSV sesuai template · Maks 10.000 baris</p>
              </div>
            </div>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={e => handleFile(e.target.files[0])} />
            <div className="flex items-start gap-2 px-4 py-3 rounded-xl w-full"
              style={{ background: 'rgba(255,214,10,0.06)', border: '1px solid rgba(255,214,10,0.15)' }}>
              <AlertCircle size={13} color="#ffd60a" className="shrink-0 mt-0.5" />
              <p className="text-[9px]" style={{ color: 'var(--text-dim)' }}>
                Download template CSV terlebih dahulu. Kolom <span style={{ color: '#ffd60a' }}>nik</span> wajib diisi. Data yang sudah ada (NIK sama) akan diperbarui otomatis (upsert).
              </p>
            </div>
          </div>
        )}

        {/* Step: Preview */}
        {step === 'preview' && (
          <div className="flex-1 overflow-auto c-scroll">
            <table className="w-full text-[9px]" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--color-surface)', position: 'sticky', top: 0, zIndex: 1 }}>
                  {['#','NIK','Nama','Tgl Lahir','JK','Kota','Status'].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-black uppercase tracking-wider" style={{ color: 'var(--text-mute)', borderBottom: '1px solid var(--border-dim)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const cfg = STATUS_CFG[r.status_intelijen] || STATUS_CFG.CLEAR;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border-lo)' }}>
                      <td className="px-3 py-2 font-mono" style={{ color: 'var(--text-mute)' }}>{i + 1}</td>
                      <td className="px-3 py-2 font-mono" style={{ color: 'var(--text-dim)' }}>{r.nik}</td>
                      <td className="px-3 py-2 font-bold" style={{ color: 'var(--text-hi)' }}>{r.nama_lengkap}</td>
                      <td className="px-3 py-2" style={{ color: 'var(--text-dim)' }}>{r.tanggal_lahir}</td>
                      <td className="px-3 py-2" style={{ color: 'var(--text-dim)' }}>{r.jenis_kelamin === 'LAKI-LAKI' ? 'L' : 'P'}</td>
                      <td className="px-3 py-2 truncate max-w-28" style={{ color: 'var(--text-dim)' }}>{r.kota_kabupaten}</td>
                      <td className="px-3 py-2">
                        <span className="px-2 py-0.5 rounded-full font-black" style={{ background: cfg.bg, color: cfg.color }}>{getIntelLabel(r.status_intelijen || 'CLEAR', lang)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Step: Done */}
        {step === 'done' && results && (
          <div className="flex-1 flex flex-col items-center justify-center gap-6 p-8">
            <div className="grid grid-cols-2 gap-4 w-full max-w-sm">
              <div className="flex flex-col items-center gap-2 p-4 rounded-2xl" style={{ background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.2)' }}>
                <p className="text-3xl font-black font-mono" style={{ color: 'var(--color-green)' }}>{results.success}</p>
                <p className="text-[9px] font-bold uppercase" style={{ color: 'var(--color-green)' }}>Berhasil</p>
              </div>
              <div className="flex flex-col items-center gap-2 p-4 rounded-2xl" style={{ background: 'rgba(255,45,85,0.08)', border: '1px solid rgba(255,45,85,0.2)' }}>
                <p className="text-3xl font-black font-mono" style={{ color: '#ff2d55' }}>{results.failed}</p>
                <p className="text-[9px] font-bold uppercase" style={{ color: '#ff2d55' }}>Gagal</p>
              </div>
            </div>
            {results.failed > 0 && (
              <p className="text-[9px] text-center" style={{ color: 'var(--text-mute)' }}>
                {results.failed} data gagal diimport. NIK mungkin sudah ada atau format tidak valid.
              </p>
            )}
            <button onClick={onClose}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black"
              style={{ background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.3)', color: 'var(--color-cyan)' }}>
              <CheckCircle size={13} /> Selesai
            </button>
          </div>
        )}

        {/* Footer */}
        {step === 'preview' && (
          <div className="px-6 py-4 flex items-center justify-between shrink-0" style={{ borderTop: '1px solid var(--border-dim)' }}>
            <button onClick={() => setStep('upload')}
              className="px-4 py-2 rounded-xl text-xs font-black hover:opacity-70"
              style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)', color: 'var(--text-dim)' }}>
              Ganti File
            </button>
            <button onClick={handleImport} disabled={importing}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-black transition-all"
              style={{ background: importing ? 'rgba(0,212,255,0.06)' : 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.3)', color: 'var(--color-cyan)', opacity: importing ? 0.7 : 1 }}>
              {importing
                ? <><div className="w-3 h-3 border border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--color-cyan)', borderTopColor: 'transparent' }} /> Mengimport...</>
                : <><Download size={13} /> Import {rows.length} Data</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const STATUS_CFG = {
  WANTED:    { color: '#ff2d55', bg: 'rgba(255,45,85,0.12)',   label: 'WANTED' },
  SUSPECT:   { color: '#ff6b2b', bg: 'rgba(255,107,43,0.10)', label: 'SUSPECT' },
  WATCHLIST: { color: '#ffd60a', bg: 'rgba(255,214,10,0.10)', label: 'WATCHLIST' },
  CLEAR:     { color: 'var(--color-green)', bg: 'rgba(0,255,136,0.10)',  label: 'CLEAR' },
};

const stnkActive = (d) => d && new Date(d) > new Date();

const StatusBadge = ({ status }) => {
  const { lang } = useApp();
  const cfg = STATUS_CFG[status] || STATUS_CFG.CLEAR;
  const label = getIntelLabel(status, lang);
  return (
    <span className="text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full"
      style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}30` }}>
      {label}
    </span>
  );
};

const WargaCard = ({ w, onClick }) => {
  const cfg = STATUS_CFG[w.status_intelijen] || STATUS_CFG.CLEAR;
  // Recognition cuma jalan kalau ADA face_embedding. Foto saja TIDAK cukup —
  // jangan tampilkan badge 'siap' hanya karena ada foto (itu menyesatkan operator).
  const enrolled   = !!w.face_embedding;                       // benar2 siap Digital Forensic
  const pendingEmb = !!w.foto_url && !w.face_embedding;        // foto ada, embedding belum

  return (
    <button onClick={() => onClick(w)}
      className="w-full text-left rounded-2xl p-4 flex items-center gap-3 transition-all group hover:opacity-95"
      style={{ background: 'var(--color-card)', border: `1px solid ${w.status_intelijen === 'WANTED' ? 'rgba(255,45,85,0.2)' : 'var(--border-lo)'}` }}>
      <div className="relative shrink-0">
        {w.foto_url
          ? <img src={w.foto_url} alt={w.nama_lengkap} className="w-12 h-12 rounded-xl object-cover" style={{ border: `2px solid ${cfg.color}40` }} />
          : <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'var(--color-surface)', border: `2px solid ${cfg.color}40` }}>
              <User size={20} color={cfg.color} />
            </div>}
        <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full border-2" style={{ background: cfg.color, borderColor: 'var(--color-card)' }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <p className="text-sm font-black truncate" style={{ color: 'var(--text-hi)' }}>{w.nama_lengkap}</p>
          <StatusBadge status={w.status_intelijen} />
          {enrolled && (
            <span title="Wajah siap dipakai untuk semua fitur deteksi"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 2,
                fontSize: 7, fontFamily: 'IBM Plex Mono', fontWeight: 700,
                background: 'rgba(0,212,255,0.10)', color: 'var(--color-cyan)',
                padding: '1px 4px', borderRadius: 3, border: '1px solid rgba(0,212,255,0.3)' }}>
              <ScanFace size={8} /> DF
            </span>
          )}
          {pendingEmb && (
            <span title="Foto sudah ada, wajah sedang disiapkan oleh sistem"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 2,
                fontSize: 7, fontFamily: 'IBM Plex Mono', fontWeight: 700,
                background: 'rgba(255,214,10,0.10)', color: '#ffd60a',
                padding: '1px 4px', borderRadius: 3, border: '1px solid rgba(255,214,10,0.3)' }}>
              <ScanFace size={8} /> PROSES
            </span>
          )}
        </div>
        <p className="text-[9px] font-mono truncate" style={{ color: 'var(--text-dim)' }}>{w.nik}</p>
        <p className="text-[9px] mt-0.5 truncate" style={{ color: 'var(--text-mute)' }}>{w.kecamatan ? `${w.kecamatan}, ` : ''}{w.kota_kabupaten}</p>
      </div>
      <ChevronRight size={14} color="var(--text-mute)" className="shrink-0" />
    </button>
  );
};

// ── Shared edit input/select ──────────────────────────────────────────────────

const EField = ({ label, value, onChange, type = 'text', options, optionLabel }) => (
  <div>
    <p className="text-[8px] uppercase tracking-widest mb-1" style={{ color: 'var(--text-mute)' }}>{label}</p>
    {options
      ? <select value={value ?? ''} onChange={e => onChange(e.target.value)}
          className="w-full rounded-lg px-2 py-1.5 text-xs outline-none"
          style={{ background: 'var(--color-bg)', border: '1px solid var(--border-mid)', color: 'var(--text-mid)' }}>
          {options.map(o => <option key={o} value={o}>{optionLabel ? optionLabel(o) : o}</option>)}
        </select>
      : <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)}
          className="w-full rounded-lg px-2 py-1.5 text-xs outline-none"
          style={{ background: 'var(--color-bg)', border: '1px solid var(--border-mid)', color: 'var(--text-mid)' }} />}
  </div>
);

// ── Read-only field ───────────────────────────────────────────────────────────

const RField = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl p-3" style={{ background: 'var(--color-surface)' }}>
    <div className="flex items-center gap-1.5 mb-1">
      <Icon size={10} color="var(--text-dim)" />
      <p className="text-[8px] uppercase tracking-wider" style={{ color: 'var(--text-mute)' }}>{label}</p>
    </div>
    <p className="text-xs font-bold truncate" style={{ color: 'var(--text-mid)' }}>{value || '-'}</p>
  </div>
);


// ── Kendaraan Modal ───────────────────────────────────────────────────────────

const KendaraanModal = ({ kendaraan, nikPemilik, onClose, onSaved }) => {
  const { t, lang } = useApp();
  const isNew = !kendaraan;
  const [form, setForm] = useState(kendaraan ?? { no_plat: '', merk_model: '', warna: '', masa_berlaku_stnk: '', catatan_visual: '', nik_pemilik: nikPemilik ?? '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    const { error } = isNew
      ? await supabase.from('kendaraan').insert(form)
      : await supabase.from('kendaraan').update(form).eq('no_plat', form.no_plat);
    setSaving(false);
    if (!error) {
      setSaved(true);
      onSaved(form);
      setTimeout(() => { setSaved(false); onClose(); }, 1200);
    }
  };

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center p-6" style={{ background: 'var(--overlay)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-sm rounded-3xl overflow-hidden" style={{ background: 'var(--color-card)', border: '1px solid var(--border-mid)' }}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--border-dim)' }}>
          <p className="text-xs font-black uppercase tracking-widest font-mono" style={{ color: 'var(--text-dim)' }}>{isNew ? (lang === 'id' ? 'Tambah Kendaraan' : 'Add Vehicle') : (lang === 'id' ? 'Edit Kendaraan' : 'Edit Vehicle')}</p>
          <button onClick={onClose}><X size={14} color="var(--text-dim)" /></button>
        </div>
        <div className="p-5 space-y-3">
          <EField label={t("fld_no_plat_simple")} value={form.no_plat} onChange={set('no_plat')} />
          <EField label={t("fld_merk_model")} value={form.merk_model} onChange={set('merk_model')} />
          <EField label={t("fld_warna")} value={form.warna} onChange={set('warna')} />
          <EField label={t("fld_masa_stnk")} value={form.masa_berlaku_stnk} onChange={set('masa_berlaku_stnk')} type="date" />
          <EField label={t("fld_catatan_visual")} value={form.catatan_visual} onChange={set('catatan_visual')} />
          <EField label={t("fld_nik_pemilik")} value={form.nik_pemilik} onChange={set('nik_pemilik')} />
        </div>
        <div className="px-5 pb-5">
          <button onClick={handleSave} disabled={saving}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black transition-all"
            style={{ background: saved ? 'rgba(0,255,136,0.12)' : 'rgba(0,212,255,0.12)', border: `1px solid ${saved ? 'rgba(0,255,136,0.3)' : 'rgba(0,212,255,0.3)'}`, color: saved ? 'var(--color-green)' : 'var(--color-cyan)' }}>
            {saved ? <><CheckCircle size={13} /> {t('saved_excl')}</> : saving ? t('saving_dots') : <><Save size={13} /> {t('save')}</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Warga Modal (view + edit) ─────────────────────────────────────────────────

const WargaModal = ({ warga, isNew = false, onClose, onSaved, onDeleted, kendaraanData = [], onKendaraanSaved, hfStatus = 'waking' }) => {
  const { t, lang } = useApp();
  const [isEditing, setIsEditing] = useState(isNew);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState(warga ?? {
    nik: '', nama_lengkap: '', tempat_lahir: '', tanggal_lahir: '',
    jenis_kelamin: 'LAKI-LAKI', gol_darah: '', agama: 'Islam',
    status_perkawinan: 'BELUM KAWIN', pekerjaan: '', kewarganegaraan: 'WNI',
    alamat_lengkap: '', rt_rw: '', kel_desa: '', kecamatan: '', kota_kabupaten: '',
    status_intelijen: 'CLEAR', catatan_fisik: '', foto_url: null,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [kModal, setKModal] = useState(null); // null | 'new' | kendaraan object
  const [modalTab, setModalTab] = useState('profile'); // 'profile' | 'osint'

  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    const { error } = isNew
      ? await supabase.from('penduduk').insert(form)
      : await supabase.from('penduduk').update(form).eq('nik', form.nik);
    setSaving(false);
    if (!error) {
      setSaved(true);
      onSaved?.(form);
      setTimeout(() => { setSaved(false); if (!isNew) setIsEditing(false); else onClose(); }, 1500);
    }
  };

  const handleDelete = async () => {
    if (!form.nik) return;
    const ok = window.confirm(
      lang === 'id'
        ? `Hapus PERMANEN penduduk ${form.nama_lengkap || form.nik}?\n\n` +
          `• Row penduduk\n` +
          `• Semua kendaraan terkait (FK)\n` +
          `• Foto di storage (penduduk-photos/${form.nik})\n` +
          `• Embedding di AI engine\n\n` +
          `Tindakan ini tidak bisa dibatalkan.`
        : `PERMANENTLY delete resident ${form.nama_lengkap || form.nik}?\n\n` +
          `• Resident row\n` +
          `• All related vehicles (FK)\n` +
          `• Storage photos (penduduk-photos/${form.nik})\n` +
          `• AI engine embedding\n\n` +
          `This action cannot be undone.`
    );
    if (!ok) return;
    setDeleting(true);
    try {
      // 1. Hapus kendaraan terkait dulu (FK constraint)
      await supabase.from('kendaraan').delete().eq('nik_pemilik', form.nik);
      // 2. Hapus foto di storage (best-effort)
      try {
        const { data: files } = await supabase.storage.from('penduduk-photos').list(form.nik);
        if (files?.length) {
          await supabase.storage.from('penduduk-photos').remove(files.map(f => `${form.nik}/${f.name}`));
        }
      } catch (e) { console.warn('[delete penduduk] storage cleanup:', e?.message); }
      // 3. Hapus embedding HF (best-effort, semua slot 0-5)
      for (let slot = 0; slot < 6; slot++) {
        try { await deleteFaceFromHF(form.nik, slot); } catch (_) { /* ignore */ }
      }
      // 4. Hapus row penduduk (yang utama — kalau ini gagal, lainnya jadi orphan)
      const { data: deleted, error } = await supabase.from('penduduk').delete().eq('nik', form.nik).select();
      if (error) throw error;
      if (!deleted || deleted.length === 0) {
        throw new Error(`KES Data memblokir DELETE penduduk (RLS policy).\n\nKendaraan + foto + embedding sudah dihapus, tapi row penduduk masih ada.\nHubungi admin untuk hapus manual.\nNIK: ${form.nik}`);
      }
      onDeleted?.(form.nik);
      onClose();
    } catch (e) {
      console.warn('[handleDelete] fail:', e?.message);
      alert(`Gagal hapus: ${e?.message || 'unknown error'}`);
    } finally {
      setDeleting(false);
    }
  };

  if (!warga && !isNew) return null;
  const cfg = STATUS_CFG[form.status_intelijen] || STATUS_CFG.CLEAR;
  const isHighRisk = form.status_intelijen === 'WANTED' || form.status_intelijen === 'SUSPECT';
  const kendaraan = kendaraanData.filter(k => k.nik_pemilik === form.nik);

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-6"
        style={{ background: 'var(--overlay)', backdropFilter: 'blur(4px)' }}>
        <div className="w-full max-w-lg rounded-3xl overflow-hidden animate-fadeInUp"
          style={{ background: 'var(--color-card)', border: '1px solid var(--border-mid)', maxHeight: '90vh', overflowY: 'auto' }}>

          {/* Header */}
          <div className="flex items-center gap-4 p-5"
            style={{ borderBottom: '1px solid var(--border-lo)', background: isHighRisk ? `${cfg.color}0a` : 'transparent' }}>
            <div className="shrink-0">
              {/* Foto baru hanya boleh diupload SETELAH row penduduk tersimpan —
                  kalau belum, embedding gagal tersimpan diam-diam (update 0 rows).
                  Saat isNew: tampilkan placeholder + instruksi, sembunyikan uploader. */}
              {isEditing && isNew
                ? <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-center px-1"
                    style={{ background: 'var(--color-surface)', border: '2px dashed var(--border-mid)' }}
                    title="Simpan data dulu, lalu upload foto wajah">
                    <span className="text-[7px] font-mono leading-tight" style={{ color: 'var(--text-mute)' }}>
                      Simpan dulu
                    </span>
                  </div>
                : isEditing
                ? <PhotoGallery
                    nik={form.nik}
                    profileUrl={form.foto_url}
                    onProfileChange={url => set('foto_url')(url)}
                  />
                : form.foto_url
                  ? <img src={form.foto_url} alt={form.nama_lengkap} className="w-16 h-16 rounded-2xl object-cover" style={{ border: `2px solid ${cfg.color}60` }} />
                  : <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'var(--color-surface)', border: `2px solid ${cfg.color}60` }}>
                      <User size={28} color={cfg.color} />
                    </div>}
            </div>
            <div className="flex-1 min-w-0">
              {isEditing
                ? <input value={form.nama_lengkap ?? ''} onChange={e => set('nama_lengkap')(e.target.value)}
                    placeholder="Nama Lengkap"
                    className="w-full text-base font-black bg-transparent outline-none border-b mb-1"
                    style={{ color: 'var(--text-hi)', borderColor: 'var(--border-mid)' }} />
                : <h2 className="text-base font-black mb-1" style={{ color: 'var(--text-hi)' }}>{form.nama_lengkap}</h2>}
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-xs font-mono" style={{ color: 'var(--text-dim)' }}>{form.nik || '—'}</p>
                {!isEditing && <StatusBadge status={form.status_intelijen} />}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {!isNew && !isEditing && (
                <button onClick={() => setIsEditing(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black transition-all"
                  style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.25)', color: 'var(--color-cyan)' }}>
                  <Edit2 size={10} /> Edit
                </button>
              )}
              <button onClick={onClose} className="flex items-center justify-center rounded-xl hover:opacity-70 transition-all"
                style={{ width: 32, height: 32 }}>
                <X size={14} color="var(--text-dim)" />
              </button>
            </div>
          </div>

          {/* Tab bar — Profile vs OSINT (Feature 003) */}
          {!isNew && (
            <div className="flex items-center gap-1 px-5 pt-3 shrink-0"
              style={{ borderBottom: '1px solid var(--border-lo)' }}>
              {[
                { id: 'profile', label: lang === 'id' ? 'Data Personal' : 'Personal Data' },
                { id: 'osint',   label: 'Digital Forensic' },
              ].map(tab => (
                <button key={tab.id} onClick={() => setModalTab(tab.id)}
                  className="px-4 py-2 text-[10px] font-black uppercase tracking-wider rounded-t-lg transition-all"
                  style={{
                    background:   modalTab === tab.id ? 'var(--color-surface)' : 'transparent',
                    color:        modalTab === tab.id ? 'var(--color-cyan)'    : 'var(--text-mute)',
                    borderBottom: modalTab === tab.id ? '2px solid var(--color-cyan)' : '2px solid transparent',
                  }}>
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* OSINT tab content */}
          {modalTab === 'osint' && !isNew && (
            <OsintTab key={form?.nik || 'none'} subject={form} operatorId="demo-operator" appHfStatus={hfStatus} />
          )}

          {/* Profile tab content (default) */}
          {(modalTab === 'profile' || isNew) && (
          <div className="p-5 space-y-5">

            {/* Edit mode: all fields */}
            {isEditing && (
              <>
                {isNew && (
                  <EField label={t("fld_nik")} value={form.nik} onChange={set('nik')} />
                )}
                <div className="grid grid-cols-2 gap-3">
                  <EField label={t("fld_tempat_lahir")} value={form.tempat_lahir} onChange={set('tempat_lahir')} />
                  <EField label={t("fld_tgl_lahir")} value={form.tanggal_lahir} onChange={set('tanggal_lahir')} type="date" />
                  <EField label={t("fld_jenis_kelamin")} value={form.jenis_kelamin} onChange={set('jenis_kelamin')} options={['LAKI-LAKI','PEREMPUAN']} />
                  <EField label="Gol. Darah" value={form.gol_darah} onChange={set('gol_darah')} options={['A','B','AB','O','-']} />
                  <EField label="Agama" value={form.agama} onChange={set('agama')} options={['Islam','Kristen','Katolik','Hindu','Budha','Konghucu']} />
                  <EField label="Status Kawin" value={form.status_perkawinan} onChange={set('status_perkawinan')} options={['BELUM KAWIN','KAWIN','CERAI HIDUP','CERAI MATI']} />
                  <EField label="Pekerjaan" value={form.pekerjaan} onChange={set('pekerjaan')} />
                  <EField label="Kewarganegaraan" value={form.kewarganegaraan} onChange={set('kewarganegaraan')} options={['WNI','WNA','BELANDA']} />
                </div>
                <EField label={t("fld_alamat")} value={form.alamat_lengkap} onChange={set('alamat_lengkap')} />
                <div className="grid grid-cols-2 gap-3">
                  <EField label={t("fld_rt_rw")} value={form.rt_rw} onChange={set('rt_rw')} />
                  <EField label={t("fld_kel")} value={form.kel_desa} onChange={set('kel_desa')} />
                  <EField label={t("fld_kec")} value={form.kecamatan} onChange={set('kecamatan')} />
                  <EField label="Kota/Kabupaten" value={form.kota_kabupaten} onChange={set('kota_kabupaten')} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <EField label="Status Intelijen" value={form.status_intelijen} onChange={set('status_intelijen')} options={['CLEAR','WATCHLIST','SUSPECT','WANTED']} optionLabel={(o) => getIntelLabel(o, lang)} />
                  <EField label="Catatan Fisik" value={form.catatan_fisik} onChange={set('catatan_fisik')} />
                </div>
              </>
            )}

            {/* View mode: read-only */}
            {!isEditing && (
              <>
                {isHighRisk && form.catatan_fisik && form.catatan_fisik !== 'Standar' && (
                  <div className="flex items-start gap-3 p-3 rounded-xl"
                    style={{ background: `${cfg.color}0d`, border: `1px solid ${cfg.color}33` }}>
                    <AlertTriangle size={14} color={cfg.color} className="mt-0.5 shrink-0" />
                    <div>
                      <p className="text-[9px] font-black uppercase mb-0.5" style={{ color: cfg.color }}>{t('ni_phys_note')}</p>
                      <p className="text-xs" style={{ color: 'var(--text-mid)' }}>{form.catatan_fisik}</p>
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest mb-3 font-mono" style={{ color: 'var(--text-mute)' }}>{t('ni_personal')}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <RField icon={Calendar}   label={t('ni_birth')}    value={`${form.tempat_lahir ?? ''}, ${form.tanggal_lahir ?? ''}`} />
                    <RField icon={User}        label={t('ni_gender')}   value={form.jenis_kelamin} />
                    <RField icon={Droplets}    label={t('ni_blood')}    value={form.gol_darah} />
                    <RField icon={BookOpen}    label={t('ni_religion')} value={form.agama} />
                    <RField icon={Heart}       label={t('ni_marital')}  value={form.status_perkawinan} />
                    <RField icon={Briefcase}   label={t('ni_job')}      value={form.pekerjaan} />
                    <RField icon={Globe}       label={t('ni_citizen')}  value={form.kewarganegaraan} />
                    <RField icon={CreditCard}  label="Gol. Darah"       value={form.gol_darah} />
                  </div>
                </div>
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest mb-2 font-mono" style={{ color: 'var(--text-mute)' }}>{t('ni_address')}</p>
                  <div className="rounded-xl p-3 space-y-1.5" style={{ background: 'var(--color-surface)' }}>
                    <div className="flex items-start gap-1.5">
                      <MapPin size={10} color="var(--text-dim)" className="mt-0.5 shrink-0" />
                      <p className="text-xs" style={{ color: 'var(--text-mid)' }}>{form.alamat_lengkap}</p>
                    </div>
                    {(form.rt_rw || form.kel_desa || form.kecamatan) && (
                      <p className="text-[9px] font-mono pl-4" style={{ color: 'var(--text-dim)' }}>
                        {[form.rt_rw && `RT/RW ${form.rt_rw}`, form.kel_desa, form.kecamatan].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    <p className="text-[9px] pl-4 font-bold" style={{ color: 'var(--text-dim)' }}>{form.kota_kabupaten}</p>
                  </div>
                </div>
                {form.last_seen && (
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-widest mb-2 font-mono" style={{ color: 'var(--text-mute)' }}>{t('ni_last_det')}</p>
                    <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'var(--color-surface)' }}>
                      <Eye size={14} color="var(--color-cyan)" />
                      <div>
                        <p className="text-xs font-bold" style={{ color: 'var(--text-mid)' }}>{form.lokasi_last || '-'}</p>
                        <p className="text-[9px] mt-0.5" style={{ color: 'var(--text-dim)' }}>{form.last_seen} · {form.confidence_last}%</p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Kendaraan section */}
            {!isNew && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[9px] font-black uppercase tracking-widest font-mono" style={{ color: 'var(--text-mute)' }}>{t('ni_reg_v')}</p>
                  <button onClick={() => setKModal('new')}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-[8px] font-black"
                    style={{ background: 'rgba(255,107,43,0.1)', border: '1px solid rgba(255,107,43,0.25)', color: '#ff6b2b' }}>
                    <Plus size={9} /> Tambah
                  </button>
                </div>
                {kendaraan.length > 0
                  ? <div className="space-y-2">
                      {kendaraan.map(k => {
                        const active = stnkActive(k.masa_berlaku_stnk);
                        return (
                          <div key={k.no_plat} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'var(--color-surface)' }}>
                            <Car size={14} color="#ff6b2b" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-black font-mono" style={{ color: 'var(--text-hi)' }}>{k.no_plat}</p>
                              <p className="text-[9px]" style={{ color: 'var(--text-dim)' }}>{k.merk_model} · {k.warna}</p>
                            </div>
                            <span className="text-[8px] font-black px-2 py-0.5 rounded-full shrink-0"
                              style={{ background: active ? 'rgba(0,255,136,0.1)' : 'rgba(255,45,85,0.1)', color: active ? 'var(--color-green)' : '#ff2d55' }}>
                              STNK {active ? 'AKTIF' : 'MATI'}
                            </span>
                            <button onClick={() => setKModal(k)}
                              className="flex items-center justify-center rounded-lg"
                              style={{ width: 26, height: 26, background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.2)' }}>
                              <Edit2 size={10} color="var(--color-cyan)" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  : <p className="text-[9px]" style={{ color: 'var(--text-mute)' }}>{t('veh_empty')}</p>}
              </div>
            )}
          </div>
          )}

          {/* Footer buttons */}
          {isEditing && (
            <div className="px-5 pb-5 flex gap-2">
              <button onClick={handleSave} disabled={saving || deleting}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black transition-all"
                style={{ background: saved ? 'rgba(0,255,136,0.12)' : 'rgba(0,212,255,0.12)', border: `1px solid ${saved ? 'rgba(0,255,136,0.3)' : 'rgba(0,212,255,0.3)'}`, color: saved ? 'var(--color-green)' : 'var(--color-cyan)', opacity: (saving || deleting) ? 0.7 : 1 }}>
                {saved ? <><CheckCircle size={13} /> {t('saved_excl')}</> : saving ? t('saving_dots') : <><Save size={13} /> {t('save')}</>}
              </button>
              {!isNew && (
                <>
                  <button onClick={() => { setIsEditing(false); setForm(warga); }} disabled={deleting}
                    className="px-4 py-2.5 rounded-xl text-xs font-black hover:opacity-70 transition-all"
                    style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)', color: 'var(--text-dim)' }}>
                    Batal
                  </button>
                  <button onClick={handleDelete} disabled={saving || deleting}
                    title={t('pen_title_delete')}
                    className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-black hover:opacity-80 transition-all"
                    style={{ background: 'rgba(255,45,85,0.12)', border: '1px solid rgba(255,45,85,0.35)', color: '#ff2d55', opacity: (saving || deleting) ? 0.6 : 1 }}>
                    {deleting
                      ? <><div className="w-3 h-3 border border-t-transparent rounded-full animate-spin" style={{ borderColor: '#ff2d55', borderTopColor: 'transparent' }} /> {t('deleting_dots')}</>
                      : <><Trash2 size={11} /> Hapus</>}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {kModal && (
        <KendaraanModal
          kendaraan={kModal === 'new' ? null : kModal}
          nikPemilik={form.nik}
          onClose={() => setKModal(null)}
          onSaved={(k) => { onKendaraanSaved?.(k); setKModal(null); }}
        />
      )}
    </>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const NeoIntel = ({ hfStatus = 'waking' }) => {
  const { t, lang } = useApp();
  const [pendudukData, setPendudukData] = useState([]);
  const [kendaraanData, setKendaraanData] = useState([]);
  const [tab, setTab] = useState('penduduk');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [selectedWarga, setSelectedWarga] = useState(null);
  // Digital Forensic workspace — subject yang dipilih operator untuk dianalisis full-page
  const [forensicSubject, setForensicSubject] = useState(null);

  // Listen event dari OsintTab dalam modal — auto pindah ke tab Digital Forensic
  // dengan subject pre-loaded saat operator klik tombol "Jalankan Digital Forensic"
  useEffect(() => {
    const handler = (ev) => {
      const subj = ev.detail?.subject;
      if (!subj?.nik) return;
      setSelectedWarga(null);     // Tutup modal NIK
      setForensicSubject(subj);   // Set subject untuk workspace
      setTab('forensic');         // Pindah ke tab forensic
    };
    window.addEventListener('horus:open-forensic', handler);
    return () => window.removeEventListener('horus:open-forensic', handler);
  }, []);
  const [showNewWarga, setShowNewWarga] = useState(false);
  const [showNewKendaraan, setShowNewKendaraan] = useState(false);
  const [showBatchUpload, setShowBatchUpload] = useState(false);

  useEffect(() => {
    Promise.all([
      supabase.from('penduduk').select('*').order('nama_lengkap'),
      supabase.from('kendaraan').select('*'),
    ]).then(([pRes, kRes]) => {
      if (!pRes.error) setPendudukData(pRes.data ?? []);
      if (!kRes.error) setKendaraanData(kRes.data ?? []);

      // § 4.1: Auto-open WargaModal kalau navigate dari Vision People card popup
      // dengan flag sessionStorage 'open_warga_nik' set.
      const targetNik = sessionStorage.getItem('open_warga_nik');
      if (targetNik && pRes.data) {
        const target = pRes.data.find(p => p.nik === targetNik);
        if (target) setSelectedWarga(target);
        sessionStorage.removeItem('open_warga_nik');
      }
    });
  }, []);

  const filteredPenduduk = useMemo(() => {
    const q = search.toLowerCase();
    return pendudukData.filter(w => {
      const matchSearch = !q || w.nama_lengkap?.toLowerCase().includes(q) || w.nik?.includes(q) || w.kota_kabupaten?.toLowerCase().includes(q);
      const matchStatus = filterStatus === 'ALL' || w.status_intelijen === filterStatus;
      return matchSearch && matchStatus;
    });
  }, [search, filterStatus, pendudukData]);

  const filteredKendaraan = useMemo(() => {
    const q = search.toLowerCase();
    return kendaraanData.filter(k => {
      if (!q) return true;
      const pemilik = pendudukData.find(p => p.nik === k.nik_pemilik);
      return k.no_plat?.toLowerCase().includes(q) || k.merk_model?.toLowerCase().includes(q) || pemilik?.nama_lengkap?.toLowerCase().includes(q);
    });
  }, [search, kendaraanData, pendudukData]);

  const counts = useMemo(() => ({
    wanted:    pendudukData.filter(w => w.status_intelijen === 'WANTED').length,
    suspect:   pendudukData.filter(w => w.status_intelijen === 'SUSPECT').length,
    watchlist: pendudukData.filter(w => w.status_intelijen === 'WATCHLIST').length,
    clear:     pendudukData.filter(w => w.status_intelijen === 'CLEAR').length,
  }), [pendudukData]);

  const handleWargaSaved = (updated) => {
    setPendudukData(prev => {
      const idx = prev.findIndex(p => p.nik === updated.nik);
      if (idx >= 0) return prev.map(p => p.nik === updated.nik ? updated : p);
      return [...prev, updated].sort((a, b) => (a.nama_lengkap ?? '').localeCompare(b.nama_lengkap ?? ''));
    });
    setSelectedWarga(updated);
  };

  const handleWargaDeleted = (nik) => {
    setPendudukData(prev => prev.filter(p => p.nik !== nik));
    setKendaraanData(prev => prev.filter(k => k.nik_pemilik !== nik));
    setSelectedWarga(null);
  };

  const handleKendaraanSaved = (k) => {
    setKendaraanData(prev => {
      const idx = prev.findIndex(x => x.no_plat === k.no_plat);
      if (idx >= 0) return prev.map(x => x.no_plat === k.no_plat ? k : x);
      return [...prev, k];
    });
  };

  return (
    <div className="h-full flex flex-col gap-4 p-4 overflow-y-auto c-scroll" style={{ background: 'var(--color-bg)' }}>

      <div className="grid grid-cols-4 gap-3 shrink-0">
        {[
          { label: t('ni_suspects'),  value: counts.wanted,    color: '#ff2d55' },
          { label: 'Suspect',         value: counts.suspect,   color: '#ff6b2b' },
          { label: t('ni_monitored'), value: counts.watchlist, color: '#ffd60a' },
          { label: t('ni_safe'),      value: counts.clear,     color: 'var(--color-green)' },
        ].map(s => (
          <div key={s.label} className="rounded-2xl p-4 flex items-center justify-between"
            style={{ background: 'var(--color-card)', border: `1px solid ${s.color}20` }}>
            <p className="text-xs font-bold" style={{ color: 'var(--text-dim)' }}>{s.label}</p>
            <p className="text-2xl font-black font-mono" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 shrink-0">
        <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'var(--color-card)' }}>
          {/* C-001 (Sprint 006 carryover): Vehicles tab DIHAPUS dari Profiling.
              Penanganan kendaraan ada di Datacenter, bukan Profiling page yang
              fokus ke subject (penduduk) + Digital Forensic workflow. */}
          {[[t('ni_citizens'), 'penduduk', User], [t('ni_forensic'), 'forensic', ScanFace]].map(([label, id, Icon]) => (
            <button key={id} onClick={() => setTab(id)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all"
              style={{ background: tab === id ? 'rgba(0,212,255,0.12)' : 'transparent', color: tab === id ? 'var(--color-cyan)' : 'var(--text-dim)', border: `1px solid ${tab === id ? 'rgba(0,212,255,0.3)' : 'transparent'}` }}>
              <Icon size={13} />{label}
            </button>
          ))}
        </div>

        {tab !== 'forensic' && (
          <div className="flex-1 flex items-center gap-2 px-4 rounded-xl"
            style={{ background: 'var(--color-card)', border: '1px solid var(--border-dim)', height: 40 }}>
            <Search size={13} color="var(--text-dim)" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={tab === 'penduduk' ? t('ni_search_w') : t('ni_search_k')}
              className="flex-1 bg-transparent outline-none text-xs" style={{ color: 'var(--text-mid)' }} />
            {search && <button onClick={() => setSearch('')}><X size={12} color="var(--text-dim)" /></button>}
          </div>
        )}

        {tab === 'penduduk' && (
          <div className="flex gap-1">
            {['ALL', 'WANTED', 'SUSPECT', 'WATCHLIST', 'CLEAR'].map(s => (
              <button key={s} onClick={() => setFilterStatus(s)}
                className="px-3 py-2 rounded-xl text-[9px] font-black transition-all"
                style={{
                  background: filterStatus === s ? (STATUS_CFG[s]?.bg || 'rgba(0,212,255,0.1)') : 'var(--color-card)',
                  color: filterStatus === s ? (STATUS_CFG[s]?.color || 'var(--color-cyan)') : 'var(--text-mute)',
                  border: `1px solid ${filterStatus === s ? (STATUS_CFG[s]?.color || 'var(--color-cyan)') + '40' : 'var(--border-lo)'}`,
                }}>
                {s === 'ALL' ? t('ni_all') : getIntelLabel(s, lang)}
              </button>
            ))}
          </div>
        )}

        {tab === 'penduduk' && (
          <button onClick={() => setShowBatchUpload(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-black transition-all shrink-0"
            style={{ background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.2)', color: 'var(--color-green)' }}>
            <Download size={13} /> {t('btn_import_csv')}
          </button>
        )}
        {tab !== 'forensic' && (
          <button
            onClick={() => tab === 'penduduk' ? setShowNewWarga(true) : setShowNewKendaraan(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-black transition-all shrink-0"
            style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)', color: 'var(--color-cyan)' }}>
            <Plus size={13} /> Tambah
          </button>
        )}
      </div>

      {tab === 'penduduk' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filteredPenduduk.map(w => <WargaCard key={w.nik} w={w} onClick={setSelectedWarga} />)}
          {filteredPenduduk.length === 0 && (
            <div className="col-span-3 flex flex-col items-center justify-center py-16 gap-3">
              <Search size={32} color="rgba(0,212,255,0.15)" strokeWidth={1} />
              <p className="text-sm" style={{ color: 'var(--text-mute)' }}>{t('ni_no_data')}</p>
            </div>
          )}
        </div>
      )}

      {/* C-001: kendaraan tab render block dihapus — kendaraan management ada
          di Datacenter, bukan Profiling. Tab selector juga dihapus di atas. */}

      {tab === 'forensic' && (
        <ForensicWorkspace
          subject={forensicSubject}
          onClearSubject={() => setForensicSubject(null)}
          hfStatus={hfStatus}
        />
      )}

      {selectedWarga && (
        <WargaModal
          warga={selectedWarga}
          onClose={() => setSelectedWarga(null)}
          onSaved={handleWargaSaved}
          onDeleted={handleWargaDeleted}
          kendaraanData={kendaraanData}
          onKendaraanSaved={handleKendaraanSaved}
          hfStatus={hfStatus}
        />
      )}

      {showNewWarga && (
        <WargaModal
          isNew
          onClose={() => setShowNewWarga(false)}
          onSaved={(w) => { handleWargaSaved(w); setShowNewWarga(false); }}
          kendaraanData={kendaraanData}
          hfStatus={hfStatus}
          onKendaraanSaved={handleKendaraanSaved}
        />
      )}

      {showBatchUpload && (
        <BatchUploadModal
          onClose={() => setShowBatchUpload(false)}
          onImported={(imported) => {
            setPendudukData(prev => {
              const map = new Map(prev.map(p => [p.nik, p]));
              imported.forEach(r => map.set(r.nik, r));
              return [...map.values()].sort((a, b) => (a.nama_lengkap ?? '').localeCompare(b.nama_lengkap ?? ''));
            });
          }}
        />
      )}

      {showNewKendaraan && (
        <KendaraanModal
          kendaraan={showNewKendaraan === true ? null : showNewKendaraan}
          onClose={() => setShowNewKendaraan(false)}
          onSaved={(k) => { handleKendaraanSaved(k); setShowNewKendaraan(false); }}
        />
      )}
    </div>
  );
};

// ─── Digital Forensic Workspace ──────────────────────────────────────────────
// Single entry point: operator klik "Jalankan Digital Forensic" di card subjek
// → modal tutup → auto-pindah ke tab ini dengan subject pre-loaded → semua jalan.
// Kalau tab dibuka manual tanpa subject, tampilkan instruksi cara start (no manual picker).
function ForensicWorkspace({ subject, onClearSubject, hfStatus = 'waking' }) {
  const { lang } = useApp();

  // Empty state — instruksi cara mulai (tidak ada manual picker)
  if (!subject) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-5 text-center max-w-2xl mx-auto">
        <div style={{
          width: 80, height: 80, borderRadius: '50%',
          background: 'rgba(0,212,255,0.06)',
          border: '2px solid rgba(0,212,255,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <ScanFace size={36} color="var(--color-cyan)" />
        </div>
        <div>
          <p className="text-base font-black uppercase tracking-widest mb-2" style={{ color: 'var(--color-cyan)' }}>
            {lang === 'id' ? 'Digital Forensic Idle' : 'Digital Forensic Idle'}
          </p>
          <p className="text-xs leading-relaxed max-w-md" style={{ color: 'var(--text-mid)' }}>
            {lang === 'id'
              ? 'Untuk memulai analisis, buka tab Penduduk → klik card subjek → tab Digital Forensic dalam modal → klik tombol "Jalankan Digital Forensic". Sistem akan otomatis pindah kembali ke tab ini dan menjalankan semua proses.'
              : 'To begin analysis: open Penduduk tab → click subject card → Digital Forensic tab in modal → click "Run Digital Forensic" button. System will auto-navigate back to this tab and run everything.'}
          </p>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono"
          style={{ color: 'var(--text-mute)' }}>
          <span>1️⃣ {lang === 'id' ? 'Pilih Subjek' : 'Pick Subject'}</span>
          <ChevronRight size={11} />
          <span>2️⃣ {lang === 'id' ? 'Klik Run' : 'Click Run'}</span>
          <ChevronRight size={11} />
          <span>3️⃣ {lang === 'id' ? 'Auto Pindah Sini' : 'Auto Move Here'}</span>
          <ChevronRight size={11} />
          <span style={{ color: 'var(--color-green)' }}>✓ {lang === 'id' ? 'Semua Jalan' : 'All Running'}</span>
        </div>
      </div>
    );
  }

  // Subject pre-loaded via event → render header + OsintTab dengan autoStart
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3 p-3 rounded-2xl"
        style={{ background: 'var(--color-card)', border: '1px solid var(--border-dim)' }}>
        <button onClick={onClearSubject}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[10px] font-bold transition-all"
          style={{ background: 'rgba(255,255,255,0.04)', color: 'var(--text-dim)', border: '1px solid var(--border-dim)' }}>
          <X size={11} /> {lang === 'id' ? 'Tutup Workspace' : 'Close Workspace'}
        </button>
        <div style={{ width: 40, height: 40, borderRadius: 8, overflow: 'hidden',
          border: '2px solid var(--color-cyan)' }}>
          {subject.foto_url && <img src={subject.foto_url} alt={subject.nama_lengkap}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
        </div>
        <div>
          <p className="text-sm font-black" style={{ color: 'var(--text-hi)' }}>{subject.nama_lengkap}</p>
          <p className="text-[10px] font-mono" style={{ color: 'var(--text-mute)' }}>NIK {subject.nik} · {subject.kota_kabupaten}</p>
        </div>
      </div>

      {/* key={nik} force remount saat subject ganti → reset OsintTab state ke 'never_run'
          → autoStart useEffect fires kembali → Stage 1 theater cinematic muncul.
          Tanpa key: state stuck di 'complete'/'awaiting_*' dari previous subject,
          autoStart skip karena state !== 'never_run' → "kehilangan proses Stage 1". */}
      <OsintTab key={subject?.nik || 'none'} subject={subject} operatorId="operator" autoStart appHfStatus={hfStatus} />
    </div>
  );
}

export default NeoIntel;
